export type Period = 'last_week' | 'last_month' | 'last_quarter' | 'last_year';

export interface PeriodOption {
    value: Period;
    label: string;
}

export const PERIOD_OPTIONS: PeriodOption[] = [
    { value: 'last_week', label: 'Last week' },
    { value: 'last_month', label: 'Last month' },
    { value: 'last_quarter', label: 'Last quarter' },
    { value: 'last_year', label: 'Last year' },
];

export const DEFAULT_PERIOD: Period = 'last_week';

export const isPeriod = (value: string | null): value is Period =>
    PERIOD_OPTIONS.some((option) => option.value === value);

export interface SeriesPoint {
    label: string;
    value: number;
}

/** A headline number without a chart (Documents Accessed, Tokens Consumed). */
export interface Metric {
    total: number;
    displayValue: string;
    deltaPct: number;
}

/** A headline number with its bucketed series (Hours saved, Usage Trends, Conversations). */
export interface MetricWithSeries extends Metric {
    series: SeriesPoint[];
}

export interface GoalCompletion {
    completionPct: number;
    completedRequests: number;
    failedRequests: number;
}

export interface Connector {
    id: string;
    name: string;
    sites: number;
}

export interface TopPerformer {
    rank: number;
    name: string;
    avatarUrl?: string;
    hoursSaved: number;
}

export interface AdminDashboardData {
    hoursSaved: MetricWithSeries;
    usageTrends: MetricWithSeries;
    conversations: MetricWithSeries;
    goalCompletion: GoalCompletion;
    documentsAccessed: Metric;
    tokensConsumed: Metric;
    connectors: Connector[];
    topPerformers: TopPerformer[];
}

const ADMIN_API_BASE_URL = window.location.origin + "/admin";

export const fetchAdminDashboardData = async (
    period: Period,
    token: string,
): Promise<AdminDashboardData> => {
    const response = await fetch(
        `${ADMIN_API_BASE_URL}/admin/dashboard?period=${encodeURIComponent(period)}`,
        { headers: { Authorization: `Bearer ${token}` } },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to fetch dashboard data: ${response.status} ${response.statusText}`,
        );
    }

    return (await response.json()) as AdminDashboardData;
};
